import { Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges, OnDestroy } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, FormArray, Validators } from "@angular/forms";
import moment from "moment";
import { InventorySetup } from "src/app/models/inventory-setup.model";
import { Inventory } from "src/app/models/inventory.model";
import { Receivable } from "src/app/models/receivable.model";
import { MasterService } from "src/app/services/master.service";
import { MessageService } from "src/app/services/message.service";
import { formatCount, formatCurrency, formatPercent } from "src/app/utils/format";
import { INVENTORY } from "src/app/constants/inventory";
import { clean } from "src/app/utils/numeric";
import { minValueValidator } from "src/app/utils/min-value.validator";
import { StoreService } from "src/app/services/store.service";
import { Lot } from "src/app/models/lot.model";
import { SAVE_DEBOUNCE_MILLIS } from "src/app/constants/sale";
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Form } from "src/app/models/form.model";
import { Subscription } from "rxjs"

@Component({
    selector: 'repo-modal',
    templateUrl: './repo-modal.component.html',
    styleUrls: ['./repo-modal.component.scss'],
    standalone: false
})

export class RepoModalComponent implements OnInit, OnChanges, OnDestroy {

    /** The latest receivable meta associated with the sale to be repoed. */
    @Input() receivable: Receivable | null = null

    /** The inventory setup for the lot to help determine some presets for the repossession. */
    @Input() setup: InventorySetup | null = null

    /** In order to have multiple components on a single page (tabs, etc...) we need a key to uniquely identify each one. */
    @Input() key: string | null = null

    /** The current user selected language code. */
    @Input() language: string = 'EN'

    /** A list of language translations for use in the UI. */
    @Input() words = {}

    /** Indicates to the callee that the user chose to proceed with the repo and it was successful. */
    @Output() repoed: EventEmitter<Object> = new EventEmitter()

    /** A lists of lots available for selection. */
    protected lots: Lot[] = []

    /** Form controls related to collecting repo information from the user. */
    protected form: FormGroup;

    /** Form controls related to collecting repo letters information from the user. */
    protected letterForm: FormGroup;

    /** A list of the forms (letters) for reposseion. */
    protected letters: Form[] = []

    /** The available options for a VIN certification level. */
    protected certifyLevels = INVENTORY.CERTIFY_LEVELS

    /** The available options for an odometer status. */
    protected odometerStatuses = INVENTORY.ODOMETER_TYPES

    /** The available options for an airbag status. */
    protected airbagStatuses = INVENTORY.AIRBAG_STATUSES

    /** The available options for a title condition (status). */
    protected titleConditions = INVENTORY.TITLE_CONDITIONS

    /** The milleage known at the time the inventory item was sold. */
    protected mileage: string | null = null;

    // A debounce timer for search events. Used to wait for
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    /** An array of value change and other types of subsctriptions so we can unsibscribe in on destroy. */
    private subscriptions: Subscription[] = []

    /** Helper method to consistently get the list of repo charges as an Array. */
    get charges() {
        return this.form.controls['charges'] as FormArray;
    }

    protected pdf: any = null

    /** Indicates the sale is in a state that can handle a repossession. */
    get canRepo() {
        if (!this.receivable) {
            return false
        }

        return ['sold', 'flag'].includes(this.receivable.status.toLocaleLowerCase())
    }

    /** Indicates whether or not the property found checkbox is checked.  */
    get propertyFound(): boolean {
        return (this.letterForm.get('personalPropertyFound').value)
    }

    constructor(private master: MasterService, private ms: MessageService, private fb: FormBuilder, private store: StoreService, private sanitizer: DomSanitizer) {
        this.form = this.fb.group({
            id: new FormControl<number>(0, []),
            inventoryId: new FormControl<number>(0, [Validators.required]),
            when: new FormControl<string>(moment().format('YYYY-MM-DD'), []),
            daysAvailable: new FormControl<string>('21', []),
            available: new FormControl<string>(moment().add(21, 'days').format('YYYY-MM-DD'), []),
            stockNumber: new FormControl<string>('', []),
            vinCertify: new FormControl<string>('', []),
            lastPayment: new FormControl<string>('No payment yet', []),
            acv: new FormControl<string>(formatCurrency(0), []),
            price: new FormControl<string>(formatCurrency(0), []),
            down: new FormControl<string>(formatCurrency(0), []),
            mileage: new FormControl<string>('0', []),
            odometerStatus: new FormControl<string>('', []),
            airbagStatus: new FormControl<string>('', []),
            titleStatus: new FormControl<string>('', []),
            announcement: new FormControl<string>('', []),
            buyersGuideDisclosure: new FormControl<boolean>(false, []),
            location: new FormControl<number>(0, []),
            forgiven: new FormControl<boolean>(false, []),
            surrendered: new FormControl<boolean>(false, []),
            gpsDevicesRetained: new FormControl<boolean>(false, []),
            charges: this.fb.array([])
        })

        this.add(null)

        this.letterForm = this.fb.group({
            id: new FormControl<number>(0, []),
            dated: new FormControl<string>(moment().format('YYYY-MM-DD'), []),
            principal: new FormControl<string>(formatCurrency(0), []),
            salePrice: new FormControl<string>(formatCurrency(0), []),
            ridingCash: new FormControl<string>(formatCurrency(0), []),
            balance: new FormControl<string>(formatCurrency(0), []),
            percentPrincipalPaid: new FormControl<string>(formatPercent(0), []),
            daysUntilAvailable: new FormControl<string>('21', []),
            personalPropertyFound: new FormControl<boolean>(false, []),
            daysUntilPropertyAvailable: new FormControl<string>('0', []),
            privateSellDays: new FormControl<string>('0', []),
            obligation: new FormControl<string | null>(null, []),
            publicSaleDate: new FormControl<string | null>(null, []),
            publicSaleTime: new FormControl<string | null>(null, []),
            publicSaleLocation: new FormControl<string | null>(null, [])
        })

        this.letterForm.get('principal').disable()
        this.letterForm.get('salePrice').disable()
        this.letterForm.get('ridingCash').disable()
        this.letterForm.get('balance').disable()
        this.letterForm.get('percentPrincipalPaid').disable()
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.form.get('when').valueChanges.subscribe((when) => {
                const days = clean(this.form.get('daysAvailable').value)
                if (!days || (days <= 0)) {
                    return
                }

                const available = moment(when).add(days, 'days').format('YYYY-MM-DD')
                this.form.get('available').setValue(available, {emitEvent: false})
            })
        )

        this.subscriptions.push(
            this.form.get('daysAvailable').valueChanges.subscribe((days) => {
                if ((days === null) || (days === undefined) || (days < 0)) {
                    return
                }

                const when = this.form.get('when').value
                const available = moment(when).add(days, 'days').format('YYYY-MM-DD')
                this.form.get('available').setValue(available, {emitEvent: false})
                this.letterForm.get('daysUntilAvailable').setValue(days, {emitEvent: false})
            })
        )

        this.subscriptions.push(
            this.letterForm.get('principal').valueChanges.subscribe((amount) => {
                const principal = clean(amount)
                if (!this.receivable || (this.receivable.price <= 0) || (principal <= 0)) {
                    this.letterForm.get('percentPrincipalPaid').setValue(formatPercent(0), {emitEvent: false})
                    return
                }

                this.letterForm.get('percentPrincipalPaid').setValue(formatPercent(this.receivable.percentPrincipalPaid), {emitEvent: false})
            })
        )

        this.subscriptions.push(
            this.form.valueChanges.subscribe((_) => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                this.watcher = setTimeout(() => {
                    this.save()
                }, SAVE_DEBOUNCE_MILLIS)
            })
        )

        this.subscriptions.push(
            this.letterForm.valueChanges.subscribe((_) => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                this.watcher = setTimeout(() => {
                    this.save()
                }, SAVE_DEBOUNCE_MILLIS)
            })
        )
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.receivable) {
            if ((changes.receivable.previousValue !== undefined) && (changes.receivable.previousValue !== null)) {
                this.form.reset()
                this.letterForm.reset()
            }

            if (this.receivable.prevPaymentDate) {
                this.form.get('lastPayment').setValue(moment(this.receivable.prevPaymentDate).format('MM/DD/YYYY'), {emitEvent: false})
            }
            else {
                this.form.get('lastPayment').setValue('No payment yet', {emitEvent: false})
            }

            this.form.patchValue({
                when: moment().format('YYYY-MM-DD'),
                daysAvailable: '21',
                available: moment().add(21, 'days').format('YYYY-MM-DD'),
                buyersGuideDisclosure: false,
                forgiven: false,
                surrendered: false,
                gpsDevicesRetained: false
            }, {emitEvent: false})

            this.letterForm.patchValue({
                dated: moment().format('YYYY-MM-DD'),
                daysUntilAvailable: '21',
                salePrice: formatCurrency(this.receivable.price),
                principal: formatCurrency(this.receivable.principalPaid + this.receivable.cashDown + this.receivable.tradeNet),
                balance: formatCurrency(this.receivable.payoff),
                ridingCash: formatCurrency(this.receivable.ridingCash),
                percentPrincipalPaid: formatPercent(this.receivable.percentPrincipalPaid),
                personalPropertyFound: false
            }, {emitEvent: false})

            this.determineLot()
            this.determineStockNumber()
            this.fetchInventory()
            this.fetchLetters()
            this.fetch()
        }

        if (changes.setup) {
            this.form.get('vinCertify').setValue(this.setup.defaultCertifyLevel, {emitEvent: false})
            this.determineStockNumber()
        }
    }

    ngOnDestroy(): void {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }

        if (this.watcher) {
            clearTimeout(this.watcher)
            this.watcher = null
        }
    }

    private determineLot = () => {
        if (!this.store || !this.store.userAccount || !this.store.userAccount['user'] || !this.store.userAccount['user'].lots) {
            return
        }

        this.lots = this.store.userAccount['user'].lots

        const lotId = parseInt(localStorage.getItem('lot') || this.store.lotSelected)
        this.form.get('location').setValue(lotId, {emitEvent: false})
    }

    /**
     *
     */
    private fetch = () => {
        if (!this.receivable || !this.receivable.inventoryId || (!this.receivable.saleId)) {
            return
        }

        this.master.get(`sales/${this.receivable.saleId}/repos/${this.receivable.inventoryId}`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.repo) {
                return
            }

            const repo = res.data.repo
            this.form.patchValue({
                id: repo.id,
                when: repo.repoed,
                daysAvailable: repo.days,
                available: moment(repo.repoed).add(repo.days, 'days').format('YYYY-MM-DD'),
                buyersGuideDisclosure: repo.disclosure,
                forgiven: repo.forgiven,
                surrendered: repo.surrendered,
                gpsDevicesRetained: repo.gpsRetained
            }, {emitEvent: false})

            if (repo.charges && repo.charges.length > 0) {
                for (const charge of repo.charges) {
                    const group = this.fb.group({
                        amount: new FormControl<string>(formatCurrency(charge.amount), [Validators.required]),
                        type: new FormControl<string>('other', []),
                        vendor: new FormControl<number>(null, [])
                    })

                    this.charges.push(group)
                }
            }

            this.letterForm.patchValue({
                id: repo.id,
                personalPropertyFound: repo.personalProperty,
                daysUntilPropertyAvailable: repo.propertyHoldDays,
                privateSellDays: repo.privateSellDays,
                obligation: repo.obligation,
                pubilcSaleDate: repo.pubilcSaleDate,
                pubilcSaleTime: repo.pubilcSaleTime,
                pubilcSaleLocation: repo.pubilcSaleLocation
            }, {emitEvent: false})
        })
    }

    /**
     *
     */
    private save = () => {
        if (!this.receivable || this.form.invalid || this.letterForm.invalid) {
            return
        }

        const mods = {
            repoed: this.form.get('when').value,
            days: this.form.get('daysAvailable').value,
            disclosure: this.form.get('buyersGuideDisclosure').value,
            forgiven: this.form.get('forgiven').value,
            surrendered: this.form.get('surrendered').value,
            gpsRetained: this.form.get('gpsDevicesRetained').value,
            charges: null,
            lettered: this.letterForm.get('dated').value,
            personalProperty: this.letterForm.get('personalPropertyFound').value,
            propertyHoldDays: this.letterForm.get('daysUntilPropertyAvailable').value,
            privateSellDays: this.letterForm.get('privateSellDays').value,
            obligation: this.letterForm.get('obligation').value,
            publicSaleDate: this.letterForm.get('publicSaleDate').value,
            publicSaleTime: this.letterForm.get('publicSaleTime').value,
            publicSaleLocation: this.letterForm.get('publicSaleLocation').value
        }

        for (const charge of this.charges.controls) {
            const amount = clean(charge.get('amount').value)
            if (!amount || (amount <= 0)) {
                continue
            }

            if (!mods.charges) {
                mods.charges = []
            }

            mods.charges.push({
                amount: amount,
                type: charge.get('type').value,
                vendorId: parseInt(charge.get('vendor').value)
            })
        }

        this.master.patch(`sales/${this.receivable.saleId}/repos/${this.receivable.inventoryId}`, mods, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.form.markAsPristine()
            this.letterForm.markAsPristine()
        })
    }

    /**
     * Retreive details about the inventory item being repossessed.
     */
    private fetchInventory = () => {
        if (!this.receivable || !this.receivable.inventoryId || (this.receivable.inventoryId <= 0)) {
            return
        }

        this.master.get(`inventory/${this.receivable.inventoryId}`, (res) => {
            this.setActiveTab()

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.inventory) {
                return
            }

            const inventory: Inventory = res.data.inventory

            this.form.patchValue({
                inventoryId: inventory.id,
                odometerStatus: (inventory.odometer || 'actual').toLocaleLowerCase(),
                airbagStatus: (inventory.airbagStatus || 'intact').toLocaleLowerCase(),
                titleStatus: (inventory.titleCondition || 'regular').toLocaleLowerCase(),
                vinCertify: (inventory.certifyLevel || 'v').toLocaleLowerCase()
            }, {emitEvent: false})
            this.mileage = formatCount(inventory.miles)
        })
    }


    private fetchLetters = () => {
        if (!this.receivable || !this.receivable.saleId || (this.receivable.saleId <= 0)) {
            return
        }

        this.letters = []
        this.master.get(`forms?saleId=${this.receivable.saleId}&panel=letters&module=repossession`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.forms) {
                return
            }

            this.letters = res.data.forms
        })
    }

    private determineStockNumber = () => {
        if (!this.setup || !this.receivable) {
            return
        }

        this.form.get('stockNumber').setValue(`${this.setup.stages.Repossession.prefix || ''}${this.receivable.stock}${this.setup.stages.Repossession.suffix || ''}`, {emitEvent: false})
    }

    /**
     * Remove a repo charge from the form array
     * @param index The index of the invite to remove
     */
    public discard = (index: number): void => {
        if ((index < 0) || (index > this.charges.length - 1)) {
            return
        }

        this.charges.removeAt(index)
    }

    /**
     * Add a new repo charge row to the form array
     * @param e Event that triggerd this method, if any
     */
    public add = (e): void => {
        const charge = this.fb.group({
            amount: new FormControl<string>(formatCurrency(0), [minValueValidator(0)]),
            type: new FormControl<string>('other', []),
            vendor: new FormControl<number>(null, [])
        })

        this.charges.push(charge)
    }

    /**
     * The repo process was initiated by the user, notify the callee once complete.
     * @param {Event} e The event that triggered the repo.
     */
    protected repo = (e: Event) => {
        if (this.form.invalid) {
            return
        }

        if (!this.receivable || !this.receivable.saleId || (this.receivable.saleId <= 0) ||
            (!this.receivable.bhph) || (this.receivable.status.toLowerCase() !== 'sold')) {
            return
        }

        const meta = this.form.value
        meta.acv = clean(meta.acv)
        meta.price = clean(meta.price)
        meta.down = clean(meta.down)
        meta.mileage = clean(meta.mileage)
        meta.daysAvailable = clean(meta.daysAvailable)
        delete(meta.charges)
        meta.charges = null

        for (const charge of this.charges.controls) {
            const amount = clean(charge.get('amount').value)
            if (!amount || (amount <= 0)) {
                continue
            }

            if (!meta.charges) {
                meta.charges = []
            }

            meta.charges.push({
                amount: amount,
                type: charge.get('type').value,
                vendorId: parseInt(charge.get('vendor').value)
            })
        }

        this.master.patch(`sales/${this.receivable.saleId}/repo`, meta, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.repoed.emit(meta)
        })
    }

    /**
     * Select the active tab based on the value given.
     * @param activeTab The name of the active tab to select.
     */
    protected setActiveTab = () => {
        let activeTab = 'repo-setup'
        if (!this.canRepo) {
            activeTab = 'repo-letters'
        }

        const links: NodeListOf<HTMLAnchorElement> = document.querySelectorAll<HTMLAnchorElement>('#repo-modal-tabs.nav-tabs > .nav-item > a.nav-link')
        links.forEach((link) => {
            link.classList.remove('active')
        })

        const tabs: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>('#repo-modal-tab-panes.tab-content > div.tab-pane')
        tabs.forEach((tab) => {
            tab.classList.remove('active')
            tab.classList.remove('show')
        })

        const tabLink: HTMLAnchorElement = document.getElementById(`${activeTab}-link`) as HTMLAnchorElement
        if (tabLink) {
            tabLink.classList.add('active')
        }

        const tab: HTMLElement = document.getElementById(`${activeTab}`) as HTMLElement
        if (tab) {
            tab.classList.add('active')
            tab.classList.add('show')
        }
    }

    /**
     * The user has closed the modal, let's save what we have thus far.
     * @param e
     */
    protected closed = (e: Event) => {

    }

    /**
     *
     * @param alias A form alias used to locate the actual form identity.
     */
    protected getLetter = (alias: string) => {
        if (!alias || alias.length <= 0) {
            return
        }

        if (!this.letters || (this.letters.length <= 0)) {
            return
        }

        const letter = this.letters.find((item) => item.alias === alias)
        if (!letter || !letter.id) {
            return
        }

        const id = letter.id

        this.pdf = null
        const payload = {
            forms: [
                {
                    formId: id,
                    module: 'repossession',
                    copies: 1,
                    xChange: null,
                    yChange: null
                }
            ]
        }

        const btn = document.getElementById('btn-view-pdf-modal') as HTMLButtonElement
        if (btn) {
            btn.click()
        }

        this.master.post(`forms?lotId=${this.receivable.lotId}&saleId=${this.receivable.saleId}`, payload, (meta) => {
            if (!meta || !meta.status || !meta.data || !meta.data.url) {
                this.ms.sendMessage("alert", { type: "danger", text: (meta && meta.error) ? meta.error : this.words[this.language]['apiNoResponse'] })
                return
            }

            const url: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(meta.data.url)
            if (!url) {
                this.ms.sendMessage("alert", { type: "danger", text: "Pdf meta data url is empty or null." })
                return
            }

            this.pdf = url
        })
    }
}
